import { useState, useEffect } from 'react';
import { adminApi, sectorsApi } from '@/lib/api';
import { UserCheck, UserX, Clock, Building2, MapPin, Phone, Mail, CreditCard } from 'lucide-react';

const PLAN_LABELS = {
  diagnostic: { name: 'Diagnostic', price: '99', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  pilotage: { name: 'Pilotage', price: '179', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  premium: { name: 'Premium', price: '249', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export default function PendingClientsPage() {
  const [pending, setPending] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([adminApi.pendingClients(), sectorsApi.list()]);
      setPending(p.data);
      setSectors(s.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleApprove = async (userId, sectorSlug) => {
    setProcessing(userId);
    try {
      const sector = sectors.find(s => s.slug === sectorSlug);
      await adminApi.approveClient(userId, { sector_id: sector?.id || '', zone: '' });
      load();
    } catch (err) { console.error(err); }
    setProcessing(null);
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Refuser cette demande ?')) return;
    setProcessing(userId);
    try {
      await adminApi.rejectClient(userId);
      load();
    } catch (err) { console.error(err); }
    setProcessing(null);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-[#1B2A4A]/20 border-t-[#1B2A4A] rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="pending-clients-page">
      <div>
        <h1 className="text-3xl font-bold text-[#1B2A4A] font-manrope tracking-tight">Demandes d'inscription</h1>
        <p className="text-gray-400 mt-1 text-sm">Validez ou refusez les demandes de nouveaux clients</p>
      </div>

      {pending.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Clock size={40} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Aucune demande en attente</p>
          <p className="text-gray-400 text-sm mt-1">Les nouvelles inscriptions apparaîtront ici</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pending.map(client => (
            <div key={client.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all" data-testid={`pending-client-${client.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-bold text-[#1B2A4A] font-manrope text-lg">{client.name}</h3>
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Mail size={12} /> {client.email}</span>
                    {client.phone && <span className="flex items-center gap-1"><Phone size={12} /> {client.phone}</span>}
                    {client.company_name && <span className="flex items-center gap-1"><Building2 size={12} /> {client.company_name}</span>}
                    {client.city && <span className="flex items-center gap-1"><MapPin size={12} /> {client.city}</span>}
                    {client.sector_slug && <span className="text-[#B8860B] font-medium">{client.sector_slug}</span>}
                    {(() => { const pl = PLAN_LABELS[client.plan] || PLAN_LABELS.diagnostic; return (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${pl.color}`} data-testid={`plan-badge-${client.id}`}>
                        <CreditCard size={10} /> {pl.name} ({pl.price} &euro;/mois)
                      </span>
                    ); })()}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2">Inscrit le {new Date(client.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleApprove(client.id, client.sector_slug)}
                    disabled={processing === client.id}
                    className="bg-emerald-600 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-emerald-700 transition-all flex items-center gap-2 disabled:opacity-50"
                    data-testid={`approve-client-${client.id}`}
                  >
                    <UserCheck size={16} /> Approuver
                  </button>
                  <button
                    onClick={() => handleReject(client.id)}
                    disabled={processing === client.id}
                    className="bg-white text-red-600 border border-red-200 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-red-50 transition-all flex items-center gap-2 disabled:opacity-50"
                    data-testid={`reject-client-${client.id}`}
                  >
                    <UserX size={16} /> Refuser
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
